import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Download, Calculator, TrendingUp, Scale, BarChart3 } from "lucide-react";
import html2pdf from "html2pdf.js";

export default function Methodologie() {
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const handleDownloadPDF = () => {
    const element = contentRef.current;
    const opt = {
      margin: [10, 10, 10, 10],
      filename: 'Methodologie_Valorisation_Sequenti.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="methodologie-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 print:hidden">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <Button onClick={handleDownloadPDF} className="bg-accent text-white hover:bg-accent/90 px-6 py-4 shadow-sm" data-testid="download-pdf">
            <Download className="w-4 h-4 mr-2" />
            Télécharger en PDF
          </Button>
        </div>

        {/* Content for PDF */}
        <div ref={contentRef} className="bg-white p-12 rounded-lg shadow-sm border border-border/40">
          {/* Title */}
          <div className="text-center mb-16 border-b border-border/50 pb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">
              Méthodologie de Valorisation
            </h1>
            <p className="text-xl text-foreground/60">Sequenti — Transmission d'entreprise</p>
            <p className="text-sm text-foreground/40 mt-4 uppercase tracking-widest font-semibold">Document confidentiel</p>
          </div>

          {/* Introduction */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-primary">
              Principe général
            </h2>
            <p className="text-foreground/70 leading-relaxed text-lg mb-8">
              Notre estimation combine <strong>3 méthodes complémentaires</strong> reconnues par les professionnels 
              de la transmission d'entreprise. Cette approche multi-critères permet d'obtenir une fourchette 
              de valorisation réaliste et défendable auprès des repreneurs et des banques.
            </p>
            <Card className="p-6 bg-muted border-none rounded-lg shadow-none">
              <p className="text-foreground/70">
                <strong className="text-primary">Important :</strong> Cette estimation est indicative et basée sur les informations déclaratives. 
                Une valorisation définitive nécessite un audit comptable et une due diligence approfondie.
              </p>
            </Card>
          </section>

          {/* Method 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Scale className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">
                Méthode 1 : Patrimoniale
              </h2>
            </div>
            <Card className="p-8 mb-6 border-none bg-muted rounded-lg shadow-none">
              <p className="font-mono text-xl text-center mb-6 bg-white py-4 rounded-md shadow-sm text-primary font-bold">
                Valorisation = (CA × 0.30) + (Résultat Net × 3)
              </p>
              <p className="text-foreground/70 text-lg leading-relaxed">
                <strong className="text-primary">Logique :</strong> On estime la valeur des actifs corporels et incorporels 
                (clientèle, marque, savoir-faire) à 30% du CA, plus 3 années de bénéfices capitalisés.
              </p>
            </Card>
            <div className="bg-primary/5 p-6 rounded-lg text-lg border border-primary/10">
              <p className="font-bold text-primary mb-3">Exemple :</p>
              <ul className="space-y-2 text-foreground/70">
                <li className="flex justify-between"><span>CA</span> <span className="font-mono">1 000 000 €</span></li>
                <li className="flex justify-between"><span>Résultat Net</span> <span className="font-mono">100 000 €</span></li>
                <li className="flex justify-between pt-2 border-t border-primary/10 font-bold text-primary italic">
                    <span>Valorisation</span> 
                    <span>(1M × 0.30) + (100k × 3) = 600 000 €</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Method 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">
                Méthode 2 : Rendement (Multiple de résultat)
              </h2>
            </div>
            <Card className="p-8 mb-8 border-none bg-muted rounded-lg shadow-none">
              <p className="font-mono text-xl text-center mb-6 bg-white py-4 rounded-md shadow-sm text-primary font-bold">
                Valorisation = Résultat Net × Multiple Secteur × Coef. Ancienneté
              </p>
              <p className="text-foreground/70 text-lg leading-relaxed">
                <strong className="text-primary">Logique :</strong> C'est la méthode la plus utilisée par les acquéreurs. 
                Elle répond à la question : "En combien d'années je récupère mon investissement ?"
              </p>
            </Card>

            <h3 className="text-xl font-bold text-primary mb-6">Multiples par secteur</h3>
            <div className="overflow-hidden rounded-lg border border-border/50 mb-10">
                <Table>
                <TableHeader className="bg-muted">
                    <TableRow>
                    <TableHead className="font-bold text-primary py-4">Secteur</TableHead>
                    <TableHead className="text-center font-bold text-primary py-4">Multiple</TableHead>
                    <TableHead className="font-bold text-primary py-4">Justification</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[
                        { s: "Tech / Digital", m: "5.0x", j: "Forte scalabilité, croissance potentielle" },
                        { s: "Industrie / Production", m: "4.5x", j: "Actifs lourds, barrières à l'entrée" },
                        { s: "Santé / Médical", m: "4.0x", j: "Clientèle captive, récurrence" },
                        { s: "Services B2B", m: "3.5x", j: "Moyenne marché, dépend des contrats" },
                        { s: "BTP / Bâtiment", m: "3.0x", j: "Cyclique, dépendant des commandes" },
                        { s: "Commerce / Distribution", m: "2.5x", j: "Marge faible, concurrence forte" },
                        { s: "Artisanat", m: "2.0x", j: "Dépendance au dirigeant" },
                        { s: "Restauration / Hôtellerie", m: "2.0x", j: "Forte rotation, saisonnalité" }
                    ].map((row, i) => (
                        <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-semibold py-4">{row.s}</TableCell>
                            <TableCell className="text-center font-bold text-accent py-4">{row.m}</TableCell>
                            <TableCell className="text-foreground/60 py-4">{row.j}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </div>

            <h3 className="text-xl font-bold text-primary mb-6">Coefficients d'ancienneté</h3>
            <div className="overflow-hidden rounded-lg border border-border/50">
                <Table>
                <TableHeader className="bg-muted">
                    <TableRow>
                    <TableHead className="font-bold text-primary py-4">Ancienneté</TableHead>
                    <TableHead className="text-center font-bold text-primary py-4">Coefficient</TableHead>
                    <TableHead className="font-bold text-primary py-4">Justification</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[
                        { a: "Moins de 5 ans", c: "0.90", j: "Risque plus élevé, historique court" },
                        { a: "5 - 10 ans", c: "1.00", j: "Référence standard" },
                        { a: "10 - 20 ans", c: "1.10", j: "Stabilité prouvée, clientèle fidèle" },
                        { a: "Plus de 20 ans", c: "1.15", j: "Prime à la pérennité" }
                    ].map((row, i) => (
                        <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-semibold py-4">{row.a}</TableCell>
                            <TableCell className="text-center font-bold text-accent py-4">{row.c}</TableCell>
                            <TableCell className="text-foreground/60 py-4">{row.j}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </div>
          </section>

          {/* Method 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">
                Méthode 3 : Comparative (Multiple de CA)
              </h2>
            </div>
            <Card className="p-8 mb-6 border-none bg-muted rounded-lg shadow-none">
              <p className="font-mono text-xl text-center mb-6 bg-white py-4 rounded-md shadow-sm text-primary font-bold">
                Valorisation = CA × 0.50 × Coef. Ancienneté
              </p>
              <p className="text-foreground/70 text-lg leading-relaxed">
                <strong className="text-primary">Logique :</strong> Approche simplifiée basée sur les comparables du marché. 
                Un CA se valorise généralement entre 40% et 70% selon les secteurs. 
                Nous utilisons 50% comme base de référence.
              </p>
            </Card>
          </section>

          {/* Final Calculation */}
          <section className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Calculator className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">
                Calcul de la valorisation finale
              </h2>
            </div>
            <Card className="p-8 bg-primary text-white border-none rounded-lg shadow-md">
              <div className="space-y-4 font-mono text-center text-lg">
                <p className="bg-white/10 p-3 rounded backdrop-blur-sm border border-white/10 italic">Moyenne = (Patrimoniale + Rendement + Comparative) / 3</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <p className="bg-white/10 p-3 rounded backdrop-blur-sm border border-white/10">Fourchette basse = Moyenne × 0.80</p>
                    <p className="bg-white/10 p-3 rounded backdrop-blur-sm border border-white/10">Fourchette haute = Moyenne × 1.20</p>
                </div>
              </div>
            </Card>
            <div className="bg-primary/5 p-6 rounded-lg text-lg mt-6 border border-primary/10">
              <p className="font-bold text-primary mb-3">Exemple complet :</p>
              <ul className="space-y-2 text-foreground/70 font-mono">
                <li className="flex justify-between italic text-sm"><span>Patrimoniale</span> <span>600 000 €</span></li>
                <li className="flex justify-between italic text-sm"><span>Rendement</span> <span>385 000 €</span></li>
                <li className="flex justify-between italic text-sm border-b border-primary/10 pb-2"><span>Comparative</span> <span>550 000 €</span></li>
                <li className="flex justify-between font-bold text-primary pt-2"><span>Moyenne</span> <span>511 667 €</span></li>
                <li className="flex justify-between font-bold text-accent"><span>Fourchette</span> <span>409 000 € à 614 000 €</span></li>
              </ul>
            </div>
          </section>

          {/* Reference Tables */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-primary mb-8">
              Tables de référence
            </h2>
            
            <h3 className="text-xl font-bold text-primary mb-6">Fourchettes de Chiffre d'Affaires</h3>
            <div className="overflow-hidden rounded-lg border border-border/50 mb-10">
                <Table>
                <TableHeader className="bg-muted">
                    <TableRow>
                    <TableHead className="font-bold text-primary py-4">Sélection utilisateur</TableHead>
                    <TableHead className="text-right font-bold text-primary py-4">Valeur retenue pour calcul</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[
                        { label: "Moins de 100 000 €", val: "75 000 €" },
                        { label: "100 000 € - 300 000 €", val: "200 000 €" },
                        { label: "300 000 € - 500 000 €", val: "400 000 €" },
                        { label: "500 000 € - 1 M€", val: "750 000 €" },
                        { label: "1 M€ - 3 M€", val: "2 000 000 €" },
                        { label: "3 M€ - 5 M€", val: "4 000 000 €" },
                        { label: "Plus de 5 M€", val: "7 500 000 €" }
                    ].map((row, i) => (
                        <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-semibold py-4">{row.label}</TableCell>
                            <TableCell className="text-right font-bold text-accent py-4">{row.val}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </div>

            <h3 className="text-xl font-bold text-primary mb-6">Fourchettes de Résultat Net / EBE</h3>
            <div className="overflow-hidden rounded-lg border border-border/50">
                <Table>
                <TableHeader className="bg-muted">
                    <TableRow>
                    <TableHead className="font-bold text-primary py-4">Sélection utilisateur</TableHead>
                    <TableHead className="text-right font-bold text-primary py-4">Valeur retenue pour calcul</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[
                        { label: "Négatif / Perte", val: "-50 000 €", color: "text-destructive" },
                        { label: "0 - 50 000 €", val: "25 000 €" },
                        { label: "50 000 € - 100 000 €", val: "75 000 €" },
                        { label: "100 000 € - 200 000 €", val: "150 000 €" },
                        { label: "200 000 € - 500 000 €", val: "350 000 €" },
                        { label: "Plus de 500 000 €", val: "750 000 €" }
                    ].map((row, i) => (
                        <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-semibold py-4">{row.label}</TableCell>
                            <TableCell className={`text-right font-bold py-4 ${row.color || "text-accent"}`}>{row.val}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </div>
          </section>

          {/* Adjustments */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-primary mb-8">
              Facteurs d'ajustement (analyse approfondie)
            </h2>
            <p className="text-foreground/70 mb-8 text-lg">
              Ces facteurs sont analysés lors du diagnostic complet et peuvent impacter significativement la valorisation finale :
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 bg-muted border-none rounded-lg shadow-none">
                <h4 className="text-xl font-bold text-destructive mb-6 flex items-center gap-2">
                    <div className="w-2 h-2 bg-destructive rounded-full" />
                    Facteurs de décote
                </h4>
                <ul className="text-lg text-foreground/70 space-y-4">
                  <li>• <strong>Dépendance client</strong> : 1 client &gt; 50% CA</li>
                  <li>• <strong>Dépendance dirigeant</strong> : Savoir-faire non transférable</li>
                  <li>• <strong>Actifs obsolètes</strong> : Machines anciennes</li>
                  <li>• <strong>Litiges en cours</strong> : Selon gravité</li>
                  <li>• <strong>Bail précaire</strong> : Échéance &lt; 3 ans</li>
                </ul>
              </Card>
              <Card className="p-8 bg-muted border-none rounded-lg shadow-none">
                <h4 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent rounded-full" />
                    Facteurs de prime
                </h4>
                <ul className="text-lg text-foreground/70 space-y-4">
                  <li>• <strong>Contrats récurrents</strong> : Abonnements, maintenance</li>
                  <li>• <strong>Clientèle diversifiée</strong> : &gt;50 clients actifs</li>
                  <li>• <strong>Équipe autonome</strong> : Management en place</li>
                  <li>• <strong>Brevets/Licences</strong> : Propriété intellectuelle</li>
                  <li>• <strong>Croissance</strong> : CA en hausse &gt;10%/an</li>
                </ul>
              </Card>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="border-t border-border/50 pt-12">
            <Card className="p-8 bg-primary/5 border-none rounded-lg shadow-none italic text-foreground/60 leading-relaxed">
              <h3 className="font-bold text-primary mb-4 not-italic">Avertissement</h3>
              <p className="text-lg">
                Cette méthodologie fournit une estimation indicative basée sur des moyennes de marché et des informations 
                déclaratives. Elle ne constitue pas une évaluation certifiée et ne saurait engager la responsabilité de 
                Sequenti. Une valorisation définitive nécessite une analyse approfondie incluant : audit des comptes, 
                vérification des contrats, état des actifs, analyse de la clientèle, et due diligence complète. 
                Les multiples sectoriels sont des moyennes observées sur le marché français des PME/TPE et peuvent 
                varier selon les spécificités de chaque entreprise.
              </p>
            </Card>
            <div className="text-center mt-12 text-foreground/60">
              <p className="text-2xl font-bold text-primary mb-4">Sequenti</p>
              <p className="text-lg">AURA • PACA • Occitanie</p>
              <p className="text-lg font-medium">eric@sequenti.fr • 07 57 81 01 51</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
